import { useEffect, useLayoutEffect, useRef, useState } from 'react'

import {
  LightModeSemanticColors,
  fontSizes,
} from '@enechain/ecloud-designsystem'

import { HintTooltip } from '~/components/common/HintTooltip'
import {
  RANGE_COLUMN_EXCLUDE_ROW_ATTR,
  RANGE_COLUMN_HEADER_ATTR,
} from '~/components/planSubmit/tableRangeCopy/tableRangeModel'
import { useTableRangeCopy } from '~/components/planSubmit/tableRangeCopy/useTableRangeCopy'
import {
  KOMA_DIVIDER_BORDER,
  isKomaIndexDividerAfter,
} from '~/models/komaDivider'
import { calcDailyTotal } from '~/models/planSubmission/dailyTotal'

/** 48 コマの計画値（backend 算出 or frontend 算出）。undefined は未入力（emptyLabel 表示）。 */
export type SlotValue = number | undefined

/**
 * PlanTable が描画する内訳列 1 列分（呼び出し側の page-local BreakdownColumn から変換して渡す）。
 * `label` は列見出し 2 段目に表示する解決済み文字列（取引先名 or 市場名。counterpartyName の
 * 有無に応じたフォールバックは呼び出し側で解決してから渡す）。
 */
export type PlanTableColumn = {
  key: string
  label: string
  /** 取引先コード（1 段目）。市場列では undefined（noCounterpartyCodeLabel を表示） */
  counterpartyCode: string | undefined
  slots: SlotValue[]
  diffFlags: boolean[]
  /**
   * JEPX 市場列等の固定ラベル。設定時は 1 段目に code、2 段目に名称を出す。
   * secondary があれば 2 段目は secondary（括弧を外したもの）だけにして、
   * primary + secondary の正式名は tooltip で出す。
   */
  fixedLabel?: { code: string; primary: string; secondary: string | undefined }
  /**
   * 前回提出の 48 スロット値（hover tooltip 用、plan-diff-cell-tooltip 設計書）。
   * 指定時のみ、変化セル (`diffFlags[i]`) の hover tooltip に前回値・差分を表示する。
   * 未指定 / 対応コマが undefined の場合は tooltip を出さない
   * （GenerationSalesPlanDetail 等、本 feature 未対応の呼び出し元は付けなくてよい）。
   */
  previousSlots?: SlotValue[]
}

/** ヘッダ 1 行目の単独合計列（rowSpan=2。群 colspan を持たない）。 */
type TotalColumnSpec = {
  kind: 'total'
  key: string
  /** ヘッダの色バリアント。対応する内訳群と同じ配色にする（design `th.col-total-*`）。 */
  variant: 'sales' | 'procurement'
  label: string
  values: SlotValue[]
}

/**
 * ヘッダ 1 行目の群見出し（colspan）+ 2 行目の取引先別内訳列。0 列時も群見出しと
 * 空の 1 列を描画する（`groupColumnCount`）。
 */
type GroupColumnSpec = {
  kind: 'group'
  key: string
  /** 色バリアント。sales=positive 系、procurement=info 系（design 準拠の固定配色）。 */
  variant: 'sales' | 'procurement'
  groupLabel: string
  columns: PlanTableColumn[]
}

/**
 * 合計列 / 内訳群を表示順で並べたもの。GenerationSalesPlanDetail（[販売計, 販売群, 調達計,
 * 調達群]）・ProcurementPlanDetail（[調達計, 調達群, 販売計, 販売群]、AC-PPD-002）とも
 * 各合計列をその内訳群の直前に置く順で統一しているが、group/total の対応関係は
 * ページごとに異なる（対象が発電/調達/販売のどれか）ため、並び順の決定は呼び出し側に委ねる。
 */
export type PlanTableColumnSpec = TotalColumnSpec | GroupColumnSpec

/**
 * 別画面からの遷移直後に一時強調する内訳セルの範囲 (AC-PPD-022)。
 * `columnKeys` の内訳列 × 行 index `fromIndex` 以上 `toIndexExclusive` 未満のセルを強調し、
 * 先頭のセルを表示領域へスクロールする。
 */
export type PlanTableHighlightedCells = {
  columnKeys: readonly string[]
  fromIndex: number
  toIndexExclusive: number
}

/**
 * 未解消の取引相手間ミスマッチの常時強調 (AC-PPD-009〜012)。列 key と行 index の集合だけを
 * 受け取り、取引先・側といった業務の意味は呼び出し側 (page) が解決してから渡す。
 */
export type PlanTableMismatchHighlight = {
  /** 内訳列 key → 強調する行 index (AC-PPD-009) */
  cellRows: ReadonlyMap<string, ReadonlySet<number>>
  /** 見出しを強調する内訳列 key (AC-PPD-010) */
  headColumnKeys: ReadonlySet<string>
  /** 合計列 spec key → 強調する行 index (AC-PPD-012) */
  totalRows: ReadonlyMap<string, ReadonlySet<number>>
  /** 時刻セルを強調する行 index (AC-PPD-011) */
  timeRows: ReadonlySet<number>
}

/**
 * 不整合バナーで注目中の箇所 (AC-PPD-016)。`columnKey` × `rowIndex` のセルにフォーカスリング、
 * その行に行の囲みを出す。`navigationId` が変わるたびにそのセルを表示領域の中央へスクロールする
 * (同じ箇所へ戻る「次へ」でもスクロールし直すため、位置ではなく操作の回数で発火する)。
 * `rowIndex` が undefined (コマを特定できない件) の時は、`columnKey` の列見出しを横方向に
 * 表示領域へ入れる (セルのフォーカスリング・行の囲みは出さない)。
 */
export type PlanTableMismatchFocus = {
  columnKey: string
  rowIndex: number | undefined
  navigationId: number
}

/**
 * 計画内不整合 (AC-IC-005 / 006) のコマの強調 (AC-PPD-024 / 025)。取引相手間ミスマッチ
 * (negative の枠) とは別の warning 区分で、需給バランスのセルと keyColumn のセルに枠を付ける。
 * 行 index → tooltip の文言 (業務の意味と i18n は呼び出し側が解決してから渡す)。
 */
export type PlanTableInternalInconsistency = {
  /** 需給バランスのセルを強調する行 index → tooltip */
  balanceRows: ReadonlyMap<number, string>
  /** keyColumn のセルを強調する行 index → tooltip (前回差分の tooltip と並べて出す) */
  keyRows: ReadonlyMap<number, string>
}

type Props = {
  timeLabels: readonly string[]
  timeHeaderLabel: string
  balanceHeaderLabel: string
  /** 需給バランス（各コマ）。左端固定キー列に使う */
  balances: SlotValue[]
  /**
   * 需給バランスの前回差分フラグ（各コマ）。true のコマは符号色ではなく差分色で描く
   * (AC-PPD-020)。未指定なら需給バランス列に差分色を付けない (発電販売計画詳細は未対応)。
   * `hasPreviousSubmitted` が false の時も付けない (他の列と同じ)。
   */
  balanceDiffFlags?: boolean[]
  /** 需給バランスの次に置くキー列（発電計画合計 / 需要計画値）。前回差分ハイライトを持つ。 */
  keyColumn: {
    headerLabel: string
    values: SlotValue[]
    diffFlags: boolean[]
    /** 前回提出の 48 スロット値（hover tooltip 用）。PlanTableColumn.previousSlots と同じ規約。 */
    previousValues?: SlotValue[]
  }
  /** 合計列 / 内訳群（表示順、呼び出し側が決定） */
  columnSpecs: PlanTableColumnSpec[]
  hasPreviousSubmitted: boolean
  emptyLabel: string
  noCounterpartyCodeLabel: string
  /**
   * 前回差分 hover tooltip の文言を組み立てる callback（呼び出し元が i18n 解決まで担う。
   * plan-diff-cell-tooltip 設計書 Decision 1: native `title` 属性）。undefined を返したら
   * title を付与しない（previous/current のいずれかが未定義の場合のガードは呼び出し元の
   * 責務）。opt-in は本 prop の有無のみで決まる（未指定 = 本 feature 未対応の呼び出し元）。
   */
  previousDiffTooltip?: (
    previous: SlotValue,
    current: SlotValue,
  ) => string | undefined
  /**
   * 日量合計固定行のラベル（AC-PLC-048 / issue #6687）。時刻列（左端固定列）に出す。
   * i18n 解決は呼び出し元（各ページの thin wrapper）が担う。
   */
  dailyTotalLabel: string
  /** 日量合計固定行の hover tooltip（native `title`）。列の意味を補う。 */
  dailyTotalTooltip: string
  /**
   * 一時強調する内訳セル (AC-PPD-022)。未指定 / undefined なら強調しない。
   * 列 key か行範囲が変わった時だけ先頭セルへスクロールする。
   */
  highlightedCells?: PlanTableHighlightedCells
  /** 不整合の常時強調 (AC-PPD-009〜012)。未指定なら強調しない (発電販売計画詳細は渡さない) */
  mismatchHighlight?: PlanTableMismatchHighlight
  /** 不整合バナーで注目中の箇所 (AC-PPD-016)。未指定ならフォーカスリングを出さない */
  mismatchFocus?: PlanTableMismatchFocus
  /** 計画内不整合のコマの強調 (AC-PPD-024 / 025)。未指定なら強調しない (発電販売計画詳細は渡さない) */
  internalInconsistency?: PlanTableInternalInconsistency
}

// テーブルの色定数（GenerationSalesPlanDetail / ProcurementPlanDetail 双方の PlanTable と同一トークン方針）
const COLORS = {
  headerBg: LightModeSemanticColors.Surface.tableHeader.value,
  // zebra / 固定列 / 合計列 / sticky ヘッダの背景。design surface-secondary (#f8f8f8)。
  // sticky 越しに透けないよう不透明の値を使う (`LightModeSemanticColors.*.value` は不透明の hex。
  // UI 辞書 dictionary/tokens.md「hex を直書きせず token の値を使う」)。
  readOnlyBg: LightModeSemanticColors.Surface.secondary.value,
  editableBg: LightModeSemanticColors.Surface.primary.value,
  // 前回値から変化したセルの文字色。design `.cell-changed { color:#0e7490 }` 由来のリテラル。
  changedText: '#0e7490',
  borderColor: LightModeSemanticColors.Border.lowEmphasis.value,
  text: LightModeSemanticColors.Text.highEmphasis.value,
  midText: LightModeSemanticColors.Text.midEmphasis.value,
  lowText: LightModeSemanticColors.Text.lowEmphasis.value,
  // 調達群ヘッダ色
  procurementGroupBg: LightModeSemanticColors.Surface.infoLow.value,
  procurementGroupText: LightModeSemanticColors.Text.infoHigh.value,
  // 販売群ヘッダ色
  salesGroupBg: LightModeSemanticColors.Surface.positiveLow.value,
  salesGroupText: LightModeSemanticColors.Text.positiveHigh.value,
  // 需給バランスの符号色: 正 = positive、負 = negative、0/undefined = midEmphasis
  positiveColor: LightModeSemanticColors.Text.positiveHigh.value,
  negativeColor: LightModeSemanticColors.Text.negativeMid.value,
  // 遷移直後の一時強調 (AC-PPD-022)。専用の design token が無いため、同じ「遷移直後の
  // 一時強調」を持つ Supply48DataGrid (AC-S4D-052) と同じ accentPrimary 系を使う。
  highlightBg: LightModeSemanticColors.Surface.accentPrimaryLightMid.value,
  highlightBorder: LightModeSemanticColors.Border.accentPrimary.value,
  // 取引相手間ミスマッチの常時強調 (AC-PPD-009〜012)。design `plan/submission-detail.html`
  // の `cell-mismatch` / `col-mismatch-head` / `col-total-mismatch` の negative 系 token。
  mismatchBg: LightModeSemanticColors.Surface.negativeLow.value,
  mismatchBorder: LightModeSemanticColors.Border.negativeMid.value,
  mismatchText: LightModeSemanticColors.Text.negativeHigh.value,
  // 計画内不整合の強調 (AC-PPD-024 / 025)。design `sd-mm-bal` / `sd-mm-retail` の warning 系 token。
  // 取引相手間ミスマッチ (negative) の枠と色で分ける。
  internalBg: LightModeSemanticColors.Surface.warningLow.value,
  internalBorder: LightModeSemanticColors.Border.warningMid.value,
  // 不整合バナーで注目中の箇所 (AC-PPD-016)。design `cell-mm-focus` / `slot-highlight`。
  focusRing: LightModeSemanticColors.Focus.clarity.value,
}

const MISMATCH_SHADOW = `inset 0 0 0 2px ${COLORS.mismatchBorder}`
const INTERNAL_SHADOW = `inset 0 0 0 2px ${COLORS.internalBorder}`

/**
 * 計画内不整合の強調セルの見た目 (背景 + 枠)。凡例のスウォッチが実セルと同じ定義を使うための
 * 公開 (AC-PPD-024 / 025)。
 */
export const INTERNAL_INCONSISTENCY_SWATCH_STYLE: React.CSSProperties = {
  background: COLORS.internalBg,
  boxShadow: INTERNAL_SHADOW,
}

/** 計画内不整合の tooltip と前回差分の tooltip を並べる区切り (design の「… ／ 前回値：N」) */
const TOOLTIP_JOINER = ' ／ '

function joinTooltips(
  ...titles: Array<string | undefined>
): string | undefined {
  const present = titles.filter((v): v is string => v !== undefined)
  return present.length === 0 ? undefined : present.join(TOOLTIP_JOINER)
}

/** 注目中の行のどの位置のセルか。行全体を 1 つの枠で囲むため、両端のセルだけ縦の辺を持つ。 */
type FocusRowEdge = 'first' | 'middle' | 'last'

/**
 * 注目中の行の囲み (design `tr.slot-highlight`)。tr の outline は sticky セル・横スクロールで
 * 途切れるため、上下の辺を各セルに、左右の辺を両端のセルに分けて inset shadow で引く。
 */
function focusRowShadow(edge: FocusRowEdge | undefined): string | undefined {
  if (edge === undefined) return undefined
  const c = COLORS.focusRing
  const base = `inset 0 2px 0 ${c}, inset 0 -2px 0 ${c}`
  if (edge === 'first') return `${base}, inset 2px 0 0 ${c}`
  if (edge === 'last') return `${base}, inset -2px 0 0 ${c}`
  return base
}

/** 複数の inset shadow を重ねる (undefined は除く)。1 つも無ければ undefined。 */
function joinShadows(
  ...shadows: Array<string | undefined>
): string | undefined {
  const present = shadows.filter((v): v is string => v !== undefined)
  return present.length === 0 ? undefined : present.join(', ')
}

/**
 * 時刻以外の列は**すべて同じ幅**にする (#7469「時刻以外の列幅を均一に揃える」)。
 *
 * 84px から 120px へ広げた。 84px は列見出しが収まらず、
 * 「需給バラン…」「調達計画値合…」のように**語の途中で切れる**うえ、
 * 長い取引先名は隣の列へはみ出していた (実ブラウザで実測)。
 * 必要幅の実測値は 需給バランス 104px / 調達計画値合計 118px で、
 * これを満たす最小の切りのよい値として 120px を採る。
 * design `plan/submission-detail.html` も同じ 120px に揃えてある。
 */
const CELL_WIDTH = 120
const TIME_COL_WIDTH = 64
/** 合計列も内訳列と同じ幅にする (均一化の対象。別値にすると揃わない)。 */
const TOTAL_COL_WIDTH = CELL_WIDTH
const TABLE_FONT_SIZE = '0.875rem' // fontSizes.md (= 14px)
const ROW_HEIGHT = '30px'
// 2 行 thead の sticky 用: 1 行目（群ヘッダ行）の固定高さ
const GROUP_ROW_HEIGHT = 34
/**
 * 2 行目（取引先 / JEPX 市場）見出しは、どの列も「コード / 名称」の 2 段に揃えて
 * 固定高さにする。小売の切替で列の顔ぶれが変わっても見出し行の高さを変えないため
 * (#9753、design `plan/submission-detail.html` の `renderPartnerHeader`)。
 */
const COUNTERPARTY_HEADER_LINE_HEIGHT = 16
const COUNTERPARTY_HEADER_HEIGHT = COUNTERPARTY_HEADER_LINE_HEIGHT * 2
/** 見出しの各行は折り返さず、列幅に収まらない分は省略記号で切る。 */
const COUNTERPARTY_HEADER_LINE_STYLE: React.CSSProperties = {
  lineHeight: `${COUNTERPARTY_HEADER_LINE_HEIGHT}px`,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}
/** 「(相対契約分)」のような外側の括弧を外す。 */
function stripEnclosingParens(text: string): string {
  return text.replace(/^\s*[(（]\s*(.*?)\s*[)）]\s*$/, '$1')
}

/**
 * 内訳群が描く列数。0 列の群も群見出しと空の 1 列を残し、小売の切替で
 * 見出し行が消えて表全体の見出し高さが変わらないようにする (#9753)。
 */
function groupColumnCount(spec: GroupColumnSpec): number {
  return Math.max(1, spec.columns.length)
}

const CELL_BORDER_RIGHT = `1px solid ${COLORS.borderColor}`

/**
 * 押すとその列 (48 コマ) を選ぶ列見出しの目印 (plan-common §2)。1 列だけを指す見出し
 * (rowSpan で最下段まで届く見出しと、群見出しの下の段) に付ける。群見出しには付けない。
 * 見出しにリンク・アイコン・並べ替えなどの操作を足す列では外す (その列は範囲選択でコピーする)。
 */
const COLUMN_HEADER_PROPS = { [RANGE_COLUMN_HEADER_ATTR]: true }
const HEADER_SHADOW = `inset 0 -1px 0 ${COLORS.borderColor}, inset -1px 0 0 ${COLORS.borderColor}`

/**
 * 縦横 sticky の重なり順。日量合計行（AC-PLC-048）が thead と tbody の間に固定される
 * ため、素の 2 段（ヘッダ / 本文）では足りず 3 段に分ける。同じ z-index だと DOM 順で
 * 後ろにある tbody 行が日量合計行の上に描画され、スクロール時に行が潜り込む。
 */
const Z_BODY_TIME = 1
const Z_DAILY_TOTAL = 2
const Z_DAILY_TOTAL_TIME = 3
const Z_HEADER = 4
const Z_HEADER_TIME = 5

/**
 * 数値フォーマット（桁区切り）。呼び出し元（page-local wrapper）が
 * `previousDiffTooltip` callback の文言組み立てにも再利用するため export する。
 */
export function formatValue(v: SlotValue, emptyLabel: string): string {
  if (v === undefined) return emptyLabel
  return v.toLocaleString()
}

/**
 * 符号付きフォーマット: +xxx / -xxx / emptyLabel。0 は符号なしで表示する。
 * 前回からの差分 (増減) 用。 需給バランスの値には使わない (正の値に「+」 を付けない、
 * AC-PLC-012 / #9692。 需給バランスは [[formatBalanceValue]] で出す)。
 */
export function formatSignedValue(v: SlotValue, emptyLabel: string): string {
  if (v === undefined) return emptyLabel
  if (v > 0) return `+${v.toLocaleString()}`
  return v.toLocaleString()
}

/**
 * 需給バランスの表示: 正の値に「+」 は付けず、 負の値にだけ「−」(U+2212) を付ける
 * (AC-PLC-012 / #9692)。 `toLocaleString` の負号は ASCII「-」 なので置き換える。
 */
export function formatBalanceValue(v: SlotValue, emptyLabel: string): string {
  if (v === undefined) return emptyLabel
  if (v < 0) return `\u2212${(-v).toLocaleString()}`
  return v.toLocaleString()
}

/** 需給バランスの符号に応じた文字色を返す。>0 = positive / <0 = negative / 0・undefined = mid */
function getBalanceColor(v: SlotValue): string {
  if (v === undefined) return COLORS.midText
  if (v > 0) return COLORS.positiveColor
  if (v < 0) return COLORS.negativeColor
  return COLORS.midText
}

function groupColors(variant: 'sales' | 'procurement'): {
  bg: string
  text: string
} {
  return variant === 'sales'
    ? { bg: COLORS.salesGroupBg, text: COLORS.salesGroupText }
    : { bg: COLORS.procurementGroupBg, text: COLORS.procurementGroupText }
}

/**
 * 読み取り専用データセル（内訳列の表示用）。前回値から変化したセルは
 * teal 文字 + 太字（bg は zebra のまま）。
 */
type DataCellProps = {
  value: SlotValue
  changed: boolean
  zebraBg: string
  emptyLabel: string
  /** 前回差分 hover tooltip の文言（native `title` 属性）。undefined なら tooltip を出さない。 */
  title?: string
  /** 遷移直後の一時強調の対象か (AC-PPD-022) */
  highlighted?: boolean
  /** 取引相手間ミスマッチの常時強調の対象か (AC-PPD-009) */
  mismatch?: boolean
  /** 計画内不整合の強調の対象か (AC-PPD-025) */
  internal?: boolean
  /** 不整合バナーで注目中のセルか (AC-PPD-016) */
  focused?: boolean
  /** 注目中の行の囲みのうち、このセルが持つ辺 */
  focusRowEdge?: FocusRowEdge
}

const DataCell = ({
  value,
  changed,
  zebraBg,
  emptyLabel,
  title,
  highlighted = false,
  mismatch = false,
  internal = false,
  focused = false,
  focusRowEdge,
}: DataCellProps): React.ReactElement => {
  const fg = changed ? COLORS.changedText : COLORS.text
  const fw = changed ? 700 : 400
  // 一時強調 (AC-PPD-022) と常時強調が重なるセルは一時強調の見た目を優先する
  // (開いた直後の目印を見せるため。一時強調が消えると常時強調に戻る)。
  const cellShadow = highlighted
    ? `inset 0 0 0 2px ${COLORS.highlightBorder}`
    : mismatch
      ? MISMATCH_SHADOW
      : internal
        ? INTERNAL_SHADOW
        : undefined

  return (
    <HintTooltip content={title}>
      <td
        // テストと scroll 先の解決に使う seam (aria 属性は td の暗黙 role に無いため data 属性)
        data-changed={changed || undefined}
        data-highlighted={highlighted || undefined}
        data-internal-inconsistency={internal || undefined}
        data-mismatch={mismatch || undefined}
        data-mismatch-focus={focused || undefined}
        style={{
          background: highlighted
            ? COLORS.highlightBg
            : internal
              ? COLORS.internalBg
              : zebraBg,
          borderRight: CELL_BORDER_RIGHT,
          boxShadow: joinShadows(cellShadow, focusRowShadow(focusRowEdge)),
          // フォーカスリングは outline で、強調の枠 (box-shadow) と重ねて描く
          outline: focused ? `2px solid ${COLORS.focusRing}` : undefined,
          outlineOffset: focused ? '-2px' : undefined,
          padding: '4px 10px',
          height: ROW_HEIGHT,
          textAlign: 'right',
          fontSize: TABLE_FONT_SIZE,
          fontWeight: fw,
          width: `${CELL_WIDTH}px`,
          minWidth: `${CELL_WIDTH}px`,
          color: fg,
          whiteSpace: 'nowrap',
        }}
        title={title}
      >
        {formatValue(value, emptyLabel)}
      </td>
    </HintTooltip>
  )
}

// 合計列（販売計 / 調達計 等）の算出セル。前回差分ハイライトは持たない。
type ReadOnlyCellProps = {
  value: SlotValue
  zebraBg: string
  emptyLabel: string
  /** その側の合計に取引相手間ミスマッチを含む行か (AC-PPD-012) */
  mismatch?: boolean
  focusRowEdge?: FocusRowEdge
}

const ReadOnlyCell = ({
  value,
  zebraBg,
  emptyLabel,
  mismatch = false,
  focusRowEdge,
}: ReadOnlyCellProps): React.ReactElement => {
  return (
    <td
      data-mismatch-total={mismatch || undefined}
      style={{
        background: mismatch ? COLORS.mismatchBg : zebraBg,
        borderRight: CELL_BORDER_RIGHT,
        boxShadow: focusRowShadow(focusRowEdge),
        padding: '4px 10px',
        height: ROW_HEIGHT,
        textAlign: 'right',
        fontSize: TABLE_FONT_SIZE,
        fontWeight: 600,
        width: `${TOTAL_COL_WIDTH}px`,
        minWidth: `${TOTAL_COL_WIDTH}px`,
        color: mismatch ? COLORS.mismatchText : COLORS.text,
      }}
    >
      {formatValue(value, emptyLabel)}
    </td>
  )
}

/**
 * 需給バランス専用セル（左端固定キー列）。符号付き表示 + 符号ベース色分け
 * （>0 = positive / <0 = negative / 0 = mid）+ secondary 背景固定 + bold(700)
 * （design col-balance）。
 *
 * 旧「差」列（末尾、zebra 背景 + weight 600、ProcurementPlanDetail のみ）は
 * AC-PPD-002 で廃止済み（procurement-plan-detail-column-order-sort-zero-retention
 * 設計書 D3）。本セルは常に左端固定キー列としてのみ使う。
 */
type BalanceCellProps = {
  value: SlotValue
  emptyLabel: string
  /** 前回値から変わったか。true なら符号色の代わりに差分色 (design `cell-changed` が色を優先) */
  changed: boolean
  /** 計画内不整合 (需給バランス ≠ 0) の強調の tooltip (AC-PPD-024)。undefined なら強調しない */
  internalTitle?: string
  focusRowEdge?: FocusRowEdge
}

const BalanceCell = ({
  value,
  emptyLabel,
  changed,
  internalTitle,
  focusRowEdge,
}: BalanceCellProps): React.ReactElement => {
  const internal = internalTitle !== undefined
  return (
    <HintTooltip content={internalTitle}>
      <td
        // 前回値から変わったか (差分色の判定)。 テストの seam
        data-changed={changed || undefined}
        data-internal-inconsistency={internal || undefined}
        style={{
          background: internal ? COLORS.internalBg : COLORS.readOnlyBg,
          borderRight: CELL_BORDER_RIGHT,
          boxShadow: joinShadows(
            internal ? INTERNAL_SHADOW : undefined,
            focusRowShadow(focusRowEdge),
          ),
          padding: '4px 10px',
          height: ROW_HEIGHT,
          textAlign: 'right',
          fontSize: TABLE_FONT_SIZE,
          width: `${TOTAL_COL_WIDTH}px`,
          minWidth: `${TOTAL_COL_WIDTH}px`,
          color: changed ? COLORS.changedText : getBalanceColor(value),
          fontWeight: 700,
        }}
        title={internalTitle}
      >
        {formatBalanceValue(value, emptyLabel)}
      </td>
    </HintTooltip>
  )
}

/**
 * 計画提出詳細ページ共通の 48 コマテーブル（read-only）。
 *
 * GenerationSalesPlanDetail / ProcurementPlanDetail の near-identical 重複実装
 * （色定数・セル style・罫線モデル・renderCounterpartyHeader）を統合したもの。
 * 列の並び順・i18n ラベル解決はページごとに異なるため `columnSpecs` として
 * 呼び出し側（各ページの thin wrapper）が決定する。
 *
 * 罫線モデル: thead th は実 border でなく inset box-shadow で下 + 右 1px（HEADER_SHADOW）。
 * tbody td は border-right のみ（横罫線なし）、行の分離は zebra（偶数行 secondary）。
 * 時刻 / 需給バランス は固定列として常に secondary 背景。需給バランス / keyColumn の
 * ヘッダは「キー列」として白背景 + bold + 右 2px。
 *
 * 規約逸脱の明示（CLAUDE.md「生 HTML 要素は原則使わず ecloud を使う」）:
 * 48 コマ × 取引先別内訳 × sticky 行列固定 の読み取りグリッドに相当する component が
 * ecloud-designsystem に無いため、raw `<table>` / `<td>` を直書きしている
 * （既存 BalanceGenerationDetail / BalanceDemandDetail の KomaTable 群と同じ慣行）。
 */
export const PlanTable = ({
  timeLabels,
  timeHeaderLabel,
  balanceHeaderLabel,
  balances,
  balanceDiffFlags,
  keyColumn,
  columnSpecs,
  hasPreviousSubmitted,
  emptyLabel,
  noCounterpartyCodeLabel,
  previousDiffTooltip,
  dailyTotalLabel,
  dailyTotalTooltip,
  highlightedCells,
  mismatchHighlight,
  mismatchFocus,
  internalInconsistency,
}: Props): React.ReactElement => {
  /**
   * 日量合計固定行（AC-PLC-048）の sticky 位置 = thead の実高さ。
   *
   * 2 行目（取引先ヘッダ）は `COUNTERPARTY_HEADER_HEIGHT` の固定高さだが、1 行目は
   * `height` が最小値でしかなく内容で育つ（下の `groupRowHeight` 参照）ので実測して追従させる。
   * 測れない環境（happy-dom 等、`getBoundingClientRect` が 0）では 0 のままとなり、
   * sticky が効かないだけで行自体は通常行として出る（表示は壊れない）。
   */
  const theadRef = useRef<HTMLTableSectionElement>(null)
  const [dailyTotalTop, setDailyTotalTop] = useState(0)

  /**
   * 時刻列を除く実際の列数 = 需給バランス + keyColumn + `columnSpecs` を展開した数。
   * `table` の幅をこの数から確定させる (幅を内容依存にすると `table-layout: fixed`
   * が無視される。 `<table>` 側のコメント参照)。
   */
  const dataColumnCount =
    2 +
    columnSpecs.reduce(
      (n, spec) => n + (spec.kind === 'group' ? groupColumnCount(spec) : 1),
      0,
    )
  /**
   * 2 行目ヘッダ（取引先）の sticky 位置 = 1 行目（群ヘッダ行）の**実高さ**。
   *
   * `GROUP_ROW_HEIGHT` (34px) は `<th>` の `height` に渡しているが、table-layout では
   * `height` は**最小値**でしかない。群ラベル (14px) + `padding: 8px 10px` で内容高が
   * 34px を超えるため、行は実際には 37px に育つ。定数を sticky の `top` にも使うと
   * **1 行目の実高さ (37) と 2 行目の吸着位置 (34) が 3px ずれ**、sticky ヘッダ群の
   * 下端 (34+64=98) が日量合計行の吸着位置 (= thead 実高 101) に届かず、
   * **その 3px からスクロール中の本文行が覗く**（内訳列だけで起きる。rowSpan=2 の
   * キー列・合計列は top:0 で thead 全高を覆うため出ない）。実測に替えて塞ぐ。
   *
   * 測れない環境（happy-dom 等）では定数のままにして、既存の描画と揃える。
   */
  const groupRowRef = useRef<HTMLTableRowElement>(null)
  const [groupRowHeight, setGroupRowHeight] = useState(GROUP_ROW_HEIGHT)

  useLayoutEffect(() => {
    const el = theadRef.current
    if (el === null) return undefined
    const groupRow = groupRowRef.current
    const update = (): void => {
      setDailyTotalTop(el.getBoundingClientRect().height)
      if (groupRow !== null) {
        const h = groupRow.getBoundingClientRect().height
        // 0 = レイアウトを持たない環境。定数を維持する。
        if (h > 0) setGroupRowHeight(h)
      }
    }
    update()
    // フォント差等で 1 行目の実高さが変わり得るため監視する。
    if (typeof ResizeObserver === 'undefined') return undefined
    const observer = new ResizeObserver(update)
    observer.observe(el)
    return () => {
      observer.disconnect()
    }
  }, [])
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const tableRef = useRef<HTMLTableElement>(null)
  // 範囲選択・列見出しでの列の選択・Ctrl+C (plan-common §2、conventions.md
  // 「Excelライクなグリッド編集」)。読取専用の列を含む表全体が対象。
  useTableRangeCopy(tableRef)
  const isHighlighted = (columnKey: string, rowIndex: number): boolean =>
    highlightedCells !== undefined &&
    rowIndex >= highlightedCells.fromIndex &&
    rowIndex < highlightedCells.toIndexExclusive &&
    highlightedCells.columnKeys.includes(columnKey)

  // AC-PPD-022: 強調範囲の先頭セルを表示領域の中央へスクロールする。発火は範囲の
  // 値 (列 key と行範囲) が変わった時だけにし、参照の同一性には依存させない
  // (強調中の再描画のたびにスクロールし直して、利用者のスクロールを引き戻さないため)。
  // happy-dom の scrollIntoView は実スクロールしないため、テストは呼び出し先を観測する。
  const highlightScrollKey =
    highlightedCells === undefined
      ? undefined
      : `${highlightedCells.fromIndex}:${highlightedCells.toIndexExclusive}:${highlightedCells.columnKeys.join('\n')}`
  useEffect(() => {
    if (highlightScrollKey === undefined) return
    const first =
      scrollContainerRef.current?.querySelector<HTMLElement>(
        '[data-highlighted]',
      )
    first?.scrollIntoView({ block: 'center', inline: 'center' })
  }, [highlightScrollKey])

  const isMismatchCell = (columnKey: string, rowIndex: number): boolean =>
    mismatchHighlight?.cellRows.get(columnKey)?.has(rowIndex) === true
  const isMismatchTotal = (specKey: string, rowIndex: number): boolean =>
    mismatchHighlight?.totalRows.get(specKey)?.has(rowIndex) === true
  const isFocusedCell = (columnKey: string, rowIndex: number): boolean =>
    mismatchFocus !== undefined &&
    mismatchFocus.columnKey === columnKey &&
    mismatchFocus.rowIndex === rowIndex
  const isFocusedHead = (columnKey: string): boolean =>
    mismatchFocus !== undefined &&
    mismatchFocus.rowIndex === undefined &&
    mismatchFocus.columnKey === columnKey

  // AC-PPD-016: 不整合バナーの前へ / 次へで注目中のセルを表示領域の中央へスクロールする。
  // 発火は操作 (navigationId) ごとにし、再描画では引き戻さない。
  const focusNavigationId = mismatchFocus?.navigationId
  useEffect(() => {
    if (focusNavigationId === undefined) return
    const container = scrollContainerRef.current
    const cell = container?.querySelector<HTMLElement>('[data-mismatch-focus]')
    if (cell !== null && cell !== undefined) {
      cell.scrollIntoView({ block: 'center', inline: 'center' })
      return
    }
    // コマを特定できない件は列見出しだけを横方向に入れる (縦位置は動かさない)
    container
      ?.querySelector<HTMLElement>('[data-mismatch-focus-head]')
      ?.scrollIntoView({ block: 'nearest', inline: 'center' })
  }, [focusNavigationId])

  const lastSpecIndex = columnSpecs.length - 1

  // 前回差分 hover tooltip の title を組み立てる。changed でない、または
  // previousDiffTooltip 未指定（呼び出し元が本 feature 未対応）なら常に undefined
  // （= tooltip を出さない）。callback の戻り値 undefined（previous/current 未定義等の
  // ガードは呼び出し元の責務）もそのまま伝播する。
  const previousDiffTitle = (
    current: SlotValue,
    previous: SlotValue,
    changed: boolean,
  ): string | undefined => {
    if (previousDiffTooltip === undefined || !changed) return undefined
    return previousDiffTooltip(previous, current)
  }

  const headerCellStyle: React.CSSProperties = {
    background: COLORS.headerBg,
    boxShadow: HEADER_SHADOW,
    padding: '8px 10px',
    fontWeight: 600,
    fontSize: TABLE_FONT_SIZE,
    color: COLORS.midText,
    whiteSpace: 'nowrap',
    textAlign: 'center',
    position: 'sticky',
    top: 0,
    // 重なり順は下から: tbody データ (なし) < tbody 時刻 (1) < 日量合計 (2) <
    // 日量合計の時刻 (3) < thead (4) < thead の時刻 (5)。日量合計行は thead の
    // 下・tbody の上に固定されるので、この 3 段を明示的に分ける必要がある。
    zIndex: Z_HEADER,
    verticalAlign: 'middle',
  }

  /**
   * 需給バランス / keyColumn のヘッダ（キー列）: 高コントラスト + bold + 右 2px。
   *
   * **地はヘッダー行と同じ `headerBg`**。 以前は `readOnlyBg` (#f8f8f8) で、 周囲の
   * ヘッダー (`Surface.tableHeader` #e7eef2) **より明るかった**ため、 色を持つ
   * 調達 (青) / 販売 (緑) の隣でこの 2 列だけが「塗り忘れ」に見え、 ヘッダーの
   * 縦の区切りが本文とずれて見えていた (2026-09-04 デザイナー指摘)。
   *
   * design `plan/submission-detail.html:78` が 2026-08-17 に同じ是正を入れている:
   * `th.col-balance, th.col-demand { background: var(--surface-table-header); ... }`。
   * 同ファイルのコメントいわく「ヘッダーは 時刻 + 主要指標 = ニュートラル帯 /
   * 調達 = 青帯 / 販売 = 緑帯 の 3 ゾーンになる。 2 列の区別は **太字 +
   * high-emphasis + 右 2px の区切り線**が持つ (色を増やさない)」。
   *
   * **TD 側の淡い地 (`readOnlyBg`) は残す** — design も「本体では列として追える方が
   * よいため」として TD 側だけ `--surface-secondary` を維持している。
   */
  const keyHeaderStyle: React.CSSProperties = {
    ...headerCellStyle,
    color: COLORS.text,
    fontWeight: 700,
    /*
     * 右の区切り線は **他の列と同じ 1px**（= `headerCellStyle` のまま上書きしない）。
     *
     * 以前はここだけ `inset -2px` で太くしていた。8080 の CSS コメントに
     * 「2 列の区別は 太字 + high-emphasis + **右 2px の区切り線**が持つ」とあるのを
     * 写したものだが、**8080 では 2px は描かれていない**（2026-09-04 デザイナー指摘）。
     *
     * `submission-detail.html:78` の宣言は `border-right-width: 2px` だけで
     * `border-style` を伴わず、実測でも `border-right: 0px none` /
     * `box-shadow: inset -1px 0 0` = **1px**。つまり不発の宣言で、コメントの
     * 「右 2px」は 8080 の実際の見た目を指していない。
     *
     * 実装だけが 2px を描いていたため、この 2 本の区切りだけ太く、しかも本文セル
     * (`CELL_BORDER_RIGHT` = 1px) と太さが変わるので、列を縦に追うとヘッダで
     * 線が太る形になっていた。キー列の区別は **太字 + high-emphasis** が持つ。
     */
  }

  /**
   * 日量合計固定行のセル style（AC-PLC-048）。
   *
   * 地は不透明（`readOnlyBg`）が必須。sticky で本文の上に浮くため、半透明だと
   * スクロールした行が透けて数字が重なって読めなくなる（COLORS.readOnlyBg の
   * 注記と同じ理由）。**色は増やさず、合計であることは太字で示す**
   * （8080 `admin/*` の合計列と同じ扱い。design 2026-08-17 の判断）。
   */
  const dailyTotalCellStyle: React.CSSProperties = {
    position: 'sticky',
    top: dailyTotalTop,
    zIndex: Z_DAILY_TOTAL,
    background: COLORS.readOnlyBg,
    // 本文との境界。thead 側は自前で下 1px を持つので、ここは下だけ引けばよい。
    boxShadow: `inset 0 -1px 0 ${COLORS.borderColor}`,
    borderRight: CELL_BORDER_RIGHT,
    padding: '4px 10px',
    height: ROW_HEIGHT,
    textAlign: 'right',
    fontSize: TABLE_FONT_SIZE,
    fontWeight: 700,
    color: COLORS.text,
    whiteSpace: 'nowrap',
  }

  /**
   * 表示中のコマ数ぶんの日量合計。1 コマでも未入力なら undefined（= emptyLabel）。
   * 根拠と鉄則は `dailyTotal.ts` の JSDoc を参照。
   */
  const dailyTotalOf = (slots: readonly SlotValue[]): SlotValue =>
    calcDailyTotal(slots, timeLabels.length)
  const dailyBalance = dailyTotalOf(balances)

  // spec AC-003（procurement-plan-detail.md / generation-sales-plan-detail.md）は
  // 「取引先名 1 行目・コード 2 行目」だが、JEPX 列との視覚的一貫性を優先して逆順にしている
  // 既知逸脱（統合前の ProcurementPlanDetail 実装の注記を継承）。spec 側との調整は PdM 確認事項。
  // 列ヘッダを描画。
  // どの列も 2 段（コード / 名称。code を低コントラスト grey の小サイズ、名称を group 色）。
  // - JEPX 固定列（fixedLabel あり）: code（JSPT3 等）/ 市場名。区分（(相対契約分) 等）を
  //   持つ列は、1 段目のコードで市場は分かるので区分だけを出し、正式名は tooltip に回す。
  // - 取引先列: コード / 取引先名
  const renderCounterpartyHeader = (
    col: PlanTableColumn,
    groupText: string,
  ): React.ReactElement => {
    // 列の種類や取引先名の長さで見出し行の高さが変わらないよう、固定高さの中で
    // 上下中央に置く (#9753)。
    const wrapperStyle: React.CSSProperties = {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      height: `${COUNTERPARTY_HEADER_HEIGHT}px`,
    }
    if (col.fixedLabel !== undefined) {
      const { code, primary, secondary } = col.fixedLabel
      const name =
        secondary !== undefined ? stripEnclosingParens(secondary) : primary
      const fullName =
        secondary !== undefined ? `${primary} ${secondary}` : primary
      return (
        <div style={wrapperStyle}>
          <div
            style={{
              ...COUNTERPARTY_HEADER_LINE_STYLE,
              fontSize: fontSizes.xs.value,
              fontWeight: 400,
              color: COLORS.lowText,
            }}
          >
            {code}
          </div>
          <div
            style={{
              ...COUNTERPARTY_HEADER_LINE_STYLE,
              fontSize: fontSizes.sm.value,
              fontWeight: 600,
              color: groupText,
            }}
            title={fullName}
          >
            {name}
          </div>
        </div>
      )
    }
    return (
      <div style={wrapperStyle}>
        <div
          style={{
            ...COUNTERPARTY_HEADER_LINE_STYLE,
            fontSize: fontSizes.xs.value,
            fontWeight: 400,
            color: COLORS.lowText,
          }}
        >
          {col.counterpartyCode ?? noCounterpartyCodeLabel}
        </div>
        {/* 列幅を CELL_WIDTH に固定したので、 収まらない取引先名は省略記号で切る
            (#7469)。 切らないと隣の列へはみ出して、 どの列の見出しか読めなくなる。
            全文は `title` (native tooltip) で読める。 */}
        <div
          style={{
            ...COUNTERPARTY_HEADER_LINE_STYLE,
            fontSize: fontSizes.sm.value,
            color: groupText,
          }}
          title={col.label}
        >
          {col.label}
        </div>
      </div>
    )
  }

  return (
    <div
      ref={scrollContainerRef}
      style={{
        overflow: 'auto',
        flex: 1,
        position: 'relative',
        border: `1px solid ${COLORS.borderColor}`,
        // eslint-disable-next-line no-restricted-syntax -- D-2 ベースライン (eclair --radius-lg)
        borderRadius: '8px',
        background: COLORS.editableBg,
      }}
    >
      <table
        ref={tableRef}
        style={{
          borderCollapse: 'collapse',
          tableLayout: 'fixed',
          // 読取専用の表なので、文字の上でも入力用の I 字カーソルにしない (列見出しは
          // globalCss の `th[data-range-column-header]` で pointer)
          cursor: 'default',
          // `width: 'max-content'` だと **`table-layout: fixed` が効かない**。
          // 表幅が内容依存だと、 Chrome は各セルの `width` を無視して内容で列を
          // 膨らませる。 実測 (この component を実ブラウザで描画):
          //   [64, 104, 90, 118, 84, 92, 84, 174, 128, 118, 84, 84]
          //   = 全列 84px 指定なのに時刻以外が **7 種類**の幅になる (#7469)。
          // 表幅を「各列の指定幅の合計」に確定させると指定どおりに揃う。
          width: `${TIME_COL_WIDTH + CELL_WIDTH * dataColumnCount}px`,
        }}
      >
        <thead ref={theadRef}>
          {/* 1 行目: 群ヘッダ行 */}
          <tr ref={groupRowRef}>
            {/* 時刻（rowspan=2, sticky 左固定） */}
            <th
              {...COLUMN_HEADER_PROPS}
              rowSpan={2}
              style={{
                ...headerCellStyle,
                left: 0,
                zIndex: Z_HEADER_TIME,
                width: `${TIME_COL_WIDTH}px`,
                minWidth: `${TIME_COL_WIDTH}px`,
                position: 'sticky',
              }}
            >
              {timeHeaderLabel}
            </th>
            {/* 需給バランス（rowspan=2, キー列） */}
            <th
              {...COLUMN_HEADER_PROPS}
              rowSpan={2}
              style={{
                ...keyHeaderStyle,
                width: `${TOTAL_COL_WIDTH}px`,
                minWidth: `${TOTAL_COL_WIDTH}px`,
              }}
            >
              {balanceHeaderLabel}
            </th>
            {/* keyColumn（発電計画合計 / 需要計画値。rowspan=2, キー列） */}
            <th
              {...COLUMN_HEADER_PROPS}
              rowSpan={2}
              style={{
                ...keyHeaderStyle,
                width: `${CELL_WIDTH}px`,
                minWidth: `${CELL_WIDTH}px`,
              }}
            >
              {keyColumn.headerLabel}
            </th>
            {/* 合計列 / 内訳群（呼び出し側が決めた表示順） */}
            {columnSpecs.map((spec) =>
              spec.kind === 'total' ? (
                <th
                  key={spec.key}
                  {...COLUMN_HEADER_PROPS}
                  rowSpan={2}
                  style={{
                    ...headerCellStyle,
                    background: groupColors(spec.variant).bg,
                    color: groupColors(spec.variant).text,
                    fontWeight: 700,
                    width: `${TOTAL_COL_WIDTH}px`,
                    minWidth: `${TOTAL_COL_WIDTH}px`,
                  }}
                >
                  {spec.label}
                </th>
              ) : (
                <th
                  key={spec.key}
                  colSpan={groupColumnCount(spec)}
                  style={{
                    ...headerCellStyle,
                    height: `${GROUP_ROW_HEIGHT}px`,
                    background: groupColors(spec.variant).bg,
                    color: groupColors(spec.variant).text,
                  }}
                >
                  {spec.groupLabel}
                </th>
              ),
            )}
          </tr>
          {/* 2 行目: 各 counterparty ヘッダ（total 列は rowspan=2 で既に埋まっている） */}
          <tr>
            {columnSpecs.map(
              (spec) =>
                spec.kind === 'group' &&
                (spec.columns.length === 0 ? (
                  <th
                    key={`${spec.key}-empty`}
                    {...COLUMN_HEADER_PROPS}
                    style={{
                      ...headerCellStyle,
                      top: groupRowHeight,
                      background: groupColors(spec.variant).bg,
                      width: `${CELL_WIDTH}px`,
                      minWidth: `${CELL_WIDTH}px`,
                    }}
                  >
                    <div
                      style={{ height: `${COUNTERPARTY_HEADER_HEIGHT}px` }}
                    />
                  </th>
                ) : (
                  spec.columns.map((col) => {
                    // AC-PPD-010: 不整合がある取引先の列見出し (design `col-mismatch-head`)
                    const mismatchHead =
                      mismatchHighlight?.headColumnKeys.has(col.key) === true
                    return (
                      <th
                        key={col.key}
                        {...COLUMN_HEADER_PROPS}
                        data-mismatch-focus-head={
                          isFocusedHead(col.key) || undefined
                        }
                        data-mismatch-head={mismatchHead || undefined}
                        style={{
                          ...headerCellStyle,
                          top: groupRowHeight,
                          background: mismatchHead
                            ? COLORS.mismatchBg
                            : groupColors(spec.variant).bg,
                          boxShadow: mismatchHead
                            ? MISMATCH_SHADOW
                            : headerCellStyle.boxShadow,
                          width: `${CELL_WIDTH}px`,
                          minWidth: `${CELL_WIDTH}px`,
                          fontWeight: 400,
                        }}
                      >
                        {renderCounterpartyHeader(
                          col,
                          mismatchHead
                            ? COLORS.mismatchText
                            : groupColors(spec.variant).text,
                        )}
                      </th>
                    )
                  })
                )),
            )}
          </tr>
        </thead>
        <tbody>
          {/*
            日量合計固定行（AC-PLC-048 / issue #6687）。00:00 行の直上に置き、
            thead の実高さぶん下げた位置に sticky で固定する。取引先との不整合
            検知時の日量読み合わせで、48 行スクロールせず総量を突合するため。
            値は表示中のコマ（フィルタ・表示モード切替後）から算出するので、
            列の出し分けにそのまま追従する。範囲選択ではコピーできるが、
            列見出しでの列の選択 (48 コマ) には含めない (plan-common §2)。
          */}
          <tr {...{ [RANGE_COLUMN_EXCLUDE_ROW_ATTR]: true }}>
            <td
              style={{
                ...dailyTotalCellStyle,
                position: 'sticky',
                left: 0,
                zIndex: Z_DAILY_TOTAL_TIME,
                textAlign: 'center',
                color: COLORS.midText,
                width: `${TIME_COL_WIDTH}px`,
                minWidth: `${TIME_COL_WIDTH}px`,
              }}
              title={dailyTotalTooltip}
            >
              {dailyTotalLabel}
            </td>
            {/* 需給バランスの日量合計（符号色。正の値に「+」 を付けない規約も各コマと同じ） */}
            <td
              style={{
                ...dailyTotalCellStyle,
                color: getBalanceColor(dailyBalance),
                width: `${TOTAL_COL_WIDTH}px`,
                minWidth: `${TOTAL_COL_WIDTH}px`,
              }}
            >
              {formatBalanceValue(dailyBalance, emptyLabel)}
            </td>
            {/* keyColumn（需要計画値 / 発電計画合計）の日量合計 */}
            <td
              style={{
                ...dailyTotalCellStyle,
                width: `${CELL_WIDTH}px`,
                minWidth: `${CELL_WIDTH}px`,
              }}
            >
              {formatValue(dailyTotalOf(keyColumn.values), emptyLabel)}
            </td>
            {/* 合計列 / 内訳列の日量合計（本体と同じ列順で描く） */}
            {columnSpecs.map((spec) =>
              spec.kind === 'total' ? (
                <td
                  key={spec.key}
                  style={{
                    ...dailyTotalCellStyle,
                    width: `${TOTAL_COL_WIDTH}px`,
                    minWidth: `${TOTAL_COL_WIDTH}px`,
                  }}
                >
                  {formatValue(dailyTotalOf(spec.values), emptyLabel)}
                </td>
              ) : spec.columns.length === 0 ? (
                <td
                  key={`${spec.key}-empty`}
                  style={{
                    ...dailyTotalCellStyle,
                    width: `${CELL_WIDTH}px`,
                    minWidth: `${CELL_WIDTH}px`,
                  }}
                />
              ) : (
                spec.columns.map((col) => (
                  <td
                    key={col.key}
                    style={{
                      ...dailyTotalCellStyle,
                      width: `${CELL_WIDTH}px`,
                      minWidth: `${CELL_WIDTH}px`,
                    }}
                  >
                    {formatValue(dailyTotalOf(col.slots), emptyLabel)}
                  </td>
                ))
              ),
            )}
          </tr>
          {timeLabels.map((label, i) => {
            // design zebra: nth-child(even)（= 1-based 偶数 = i が奇数）を secondary に。
            const zebraBg = i % 2 === 1 ? COLORS.readOnlyBg : COLORS.editableBg
            // AC-PPD-016: 注目中の行 (design `tr.slot-highlight`)
            const isFocusRow =
              mismatchFocus !== undefined && mismatchFocus.rowIndex === i
            // 行の最後のセル (= 最後の spec の最後のセル) だけが囲みの右端を持つ
            const edgeOf = (isLastCell: boolean): FocusRowEdge | undefined => {
              if (!isFocusRow) return undefined
              return isLastCell ? 'last' : 'middle'
            }
            // AC-PPD-011: 不整合がある行の時刻セル
            const mismatchTime = mismatchHighlight?.timeRows.has(i) === true
            return (
              <tr
                key={label}
                data-mismatch-focus-row={isFocusRow || undefined}
                // 3 時間 (6 コマ) ごとの区切り線 (conventions.md §48 コマの表の区切り線)。
                // border-collapse の行の線は sticky の時刻セルの下にも出る (Chrome 実測)。
                style={
                  isKomaIndexDividerAfter(i)
                    ? { borderBottom: KOMA_DIVIDER_BORDER }
                    : undefined
                }
              >
                {/* 時刻セル（sticky 左固定、固定列なので常に secondary） */}
                <td
                  data-mismatch-time={mismatchTime || undefined}
                  style={{
                    position: 'sticky',
                    left: 0,
                    zIndex: Z_BODY_TIME,
                    background: mismatchTime
                      ? COLORS.mismatchBg
                      : COLORS.readOnlyBg,
                    borderRight: CELL_BORDER_RIGHT,
                    boxShadow: focusRowShadow(isFocusRow ? 'first' : undefined),
                    padding: '4px 10px',
                    height: ROW_HEIGHT,
                    fontSize: TABLE_FONT_SIZE,
                    fontWeight: mismatchTime ? 700 : 500,
                    color: mismatchTime ? COLORS.mismatchText : COLORS.midText,
                    textAlign: 'center',
                    whiteSpace: 'nowrap',
                    width: `${TIME_COL_WIDTH}px`,
                    minWidth: `${TIME_COL_WIDTH}px`,
                  }}
                >
                  {label}
                </td>
                {/* 需給バランス（read-only、算出、符号付き色分け） */}
                <BalanceCell
                  changed={
                    hasPreviousSubmitted && (balanceDiffFlags?.[i] ?? false)
                  }
                  emptyLabel={emptyLabel}
                  focusRowEdge={isFocusRow ? 'middle' : undefined}
                  internalTitle={internalInconsistency?.balanceRows.get(i)}
                  value={balances[i]}
                />
                {/* keyColumn（read-only、前回差分ハイライトあり） */}
                <DataCell
                  changed={hasPreviousSubmitted && keyColumn.diffFlags[i]}
                  emptyLabel={emptyLabel}
                  focusRowEdge={isFocusRow ? 'middle' : undefined}
                  internal={internalInconsistency?.keyRows.has(i) === true}
                  title={joinTooltips(
                    internalInconsistency?.keyRows.get(i),
                    previousDiffTitle(
                      keyColumn.values[i],
                      keyColumn.previousValues?.[i],
                      hasPreviousSubmitted && keyColumn.diffFlags[i],
                    ),
                  )}
                  value={keyColumn.values[i]}
                  zebraBg={zebraBg}
                />
                {/* 合計列 / 内訳列（呼び出し側が決めた表示順） */}
                {columnSpecs.map((spec, specIndex) =>
                  spec.kind === 'total' ? (
                    <ReadOnlyCell
                      key={spec.key}
                      emptyLabel={emptyLabel}
                      focusRowEdge={edgeOf(specIndex === lastSpecIndex)}
                      mismatch={isMismatchTotal(spec.key, i)}
                      value={spec.values[i]}
                      zebraBg={zebraBg}
                    />
                  ) : spec.columns.length === 0 ? (
                    <td
                      key={`${spec.key}-empty`}
                      style={{
                        background: zebraBg,
                        borderRight: CELL_BORDER_RIGHT,
                        boxShadow: focusRowShadow(
                          edgeOf(specIndex === lastSpecIndex),
                        ),
                        height: ROW_HEIGHT,
                        width: `${CELL_WIDTH}px`,
                        minWidth: `${CELL_WIDTH}px`,
                      }}
                    />
                  ) : (
                    spec.columns.map((col, colIndex) => {
                      const changed =
                        hasPreviousSubmitted && (col.diffFlags[i] ?? false)
                      return (
                        <DataCell
                          key={col.key}
                          changed={changed}
                          emptyLabel={emptyLabel}
                          focusRowEdge={edgeOf(
                            specIndex === lastSpecIndex &&
                              colIndex === spec.columns.length - 1,
                          )}
                          focused={isFocusedCell(col.key, i)}
                          highlighted={isHighlighted(col.key, i)}
                          mismatch={isMismatchCell(col.key, i)}
                          title={previousDiffTitle(
                            col.slots[i],
                            col.previousSlots?.[i],
                            changed,
                          )}
                          value={col.slots[i]}
                          zebraBg={zebraBg}
                        />
                      )
                    })
                  ),
                )}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
